import { AsyncPipe, JsonPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslocoPipe } from '@jsverse/transloco';
import { Subject, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { PrettifyRulePipe, MessagesService, RuleService } from 'shared/domain';

import { AutoCompleteResponse, DIALOG_DATA, RuleValidationResult } from '@treetop/shared/model';

import { ButtonDirective } from '../../button/button.directive';
import { DialogRef } from '../../dialog/dialog-ref';
import { DialogModule } from '../../dialog/dialog.module';
import { DialogService } from '../../dialog/dialog.service';
import { IconDirective } from '../../icon/icon.directive';
import { PrettifiedRuleComponent } from '../prettified-rule/prettified-rule.component';
import { RuleHelpSectionDialogComponent } from '../rule-help-section-dialog/rule-help-section-dialog.component';

interface RuleInput {
  value: string;
  cursorPosition: number;
}

export type RuleInputDialogData = {
  rule: UntypedFormControl;
  label: string;
  prettyfiedRule: UntypedFormControl;
  publishedComponents: boolean;
};

@Component({
  selector: 'app-rule-input-dialog',
  templateUrl: './rule-input-dialog.component.html',
  standalone: true,
  styleUrls: ['./rule-input-dialog.component.scss'],
  imports: [
    MatFormFieldModule,
    TranslocoPipe,
    ReactiveFormsModule,
    MatAutocompleteModule,
    JsonPipe,
    PrettifyRulePipe,
    AsyncPipe,
    PrettifiedRuleComponent,
    MatButtonModule,
    MatInputModule,
    DialogModule,
    MatIconModule,
    IconDirective,
    ButtonDirective,
  ],
})
export class RuleInputDialogComponent {
  private ruleService = inject(RuleService);
  private messages = inject(MessagesService);
  protected dialogService = inject(DialogService);

  ruleCopy: UntypedFormControl;
  autoCompleteResponse: AutoCompleteResponse = { suggestions: [] };
  publishedComponents: boolean;

  private ruleInputSubject: Subject<RuleInput> = new Subject();

  public data: RuleInputDialogData = inject(DIALOG_DATA) as RuleInputDialogData;
  private dialogRef: DialogRef = inject(DialogRef);

  constructor() {
    this.ruleCopy = new UntypedFormControl();
    this.ruleCopy.setValue(this.data.rule);
    this.publishedComponents = this.data.publishedComponents;

    this.ruleInputSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((ruleInput) => {
          return this.ruleService.validateRule(ruleInput.value, this.data.publishedComponents).pipe(
            catchError((err) => {
              this.messages.showError('ErrorValidatingRule');
              return throwError(() => err);
            }),
          );
        }),
      )
      .subscribe((result: RuleValidationResult) => {
        if (result) {
          if (result.isValid) {
            delete this.ruleCopy.setErrors['rule'];
          } else {
            this.ruleCopy.setErrors({ rule: result });
          }
        } else {
          delete this.ruleCopy.setErrors['rule'];
        }
      });

    this.ruleInputSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((ruleInput) => {
          return this.ruleService.autocompleteRule(ruleInput.value, ruleInput.cursorPosition, this.data.publishedComponents).pipe(
            catchError((err) => {
              this.messages.showError('ErrorLoadingAutocomplete');
              return throwError(() => err);
            }),
          );
        }),
      )
      .subscribe((response: AutoCompleteResponse) => {
        this.autoCompleteResponse = response;
      });
  }

  getSuggestions(event: KeyboardEvent, ref: HTMLTextAreaElement): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      this.ruleInputSubject.next({ value: this.ruleCopy.value as string, cursorPosition: ref.selectionStart });
    }
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    const selection = event.option.value as string;
    const startIndex = this.autoCompleteResponse.cursorPosition - this.autoCompleteResponse.textToAutoComplete.length;
    const endIndex = this.autoCompleteResponse.cursorPosition;
    const startString = this.autoCompleteResponse.rule.slice(0, startIndex);
    const endString = this.autoCompleteResponse.rule.substring(endIndex);
    const finalRule = `${startString}${selection}  ${endString}`.replace(/ +/g, ' ');
    this.ruleCopy.setValue(finalRule);
    this.ruleInputSubject.next({ value: this.ruleCopy.value as string, cursorPosition: finalRule.length });
  }

  openHelpDialog(): void {
    this.dialogService.open(RuleHelpSectionDialogComponent, { width: '60%', disableClose: true });
  }

  onSave(): void {
    this.ruleService.prettifyRule(this.ruleCopy.value).subscribe((rule) => {
      this.dialogRef.close({ rule: this.ruleCopy.value, prettified: rule.prettified });
    });
  }
}
